import { COLORS } from '../../../IndividualPlan/tokens';
import type { ScoreUnit } from '../../types';
import type { ChartPoint } from './widaTrajectoryData';
import { formatSchoolYearShort } from './YearAxisTick';
import { labelForStartYear, schoolYearStartYear } from '../../../../core/schoolYear';

type WidaCurrentScalePanelProps = {
  points: ChartPoint[];
  yDomain?: [number, number];
  yTicks?: number[];
  height?: number;
  width?: number;
  fontSize?: number;
  variant?: 'default' | 'perDomain';
  unit?: ScoreUnit;
  accentColor?: string;
};

const FUTURE_SLOTS = 3;

function resolveDomain(points: ChartPoint[], yDomain?: [number, number]): [number, number] {
  if (yDomain) return yDomain;
  const values = points.map((p) => p.value);
  if (values.length === 0) return [0, 1];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return [min - 1, max + 1];
  const pad = (max - min) * 0.5;
  return [min - pad, max + pad];
}

function projectSchoolYear(baseYear: string, offset: number): string {
  const start = schoolYearStartYear(baseYear);
  return start == null ? '' : labelForStartYear(start + offset);
}

function slotSchoolYear(points: ChartPoint[], index: number): string {
  if (index < points.length) return points[index]?.schoolYear ?? '';
  const last = points[points.length - 1];
  if (!last) return '';
  return projectSchoolYear(last.schoolYear, index - (points.length - 1));
}

export function WidaCurrentScalePanel({
  points,
  yDomain,
  yTicks,
  height = 260,
  width = 360,
  fontSize = 16,
  variant = 'default',
  unit = 'proficiency_level',
  accentColor = COLORS.blue,
}: WidaCurrentScalePanelProps) {
  const perDomain = variant === 'perDomain';
  const left = perDomain ? 34 : 56;
  const right = perDomain ? 34 : 16;
  const top = perDomain ? 34 : 24;
  const bottom = perDomain ? 34 : 40;
  const innerW = width - left - right;
  const innerH = height - top - bottom;

  const [yMin, yMax] = resolveDomain(points, yDomain);
  const slotCount = Math.max(points.length, FUTURE_SLOTS);

  const x = (index: number) => {
    if (slotCount <= 1) return left + innerW / 2;
    return left + (index / (slotCount - 1)) * innerW;
  };
  const y = (value: number) =>
    yMax === yMin ? top + innerH / 2 : top + (1 - (value - yMin) / (yMax - yMin)) * innerH;

  const dotRadius = 7;
  const valueGap = dotRadius + fontSize * 0.6;
  const formatValue = (value: number) =>
    unit === 'scale_score' ? String(Math.round(value)) : value.toFixed(1);

  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ height: 'auto', maxHeight: height, display: 'block' }}
        role="img"
        aria-label="WIDA current-scale trajectory"
      >
        {!perDomain &&
          (yTicks ?? []).map((t) => (
            <line
              key={`grid-${t}`}
              x1={left}
              y1={y(t)}
              x2={width - right}
              y2={y(t)}
              stroke={COLORS.hairlineSoft}
            />
          ))}

        {!perDomain && (
          <line
            x1={left}
            y1={top}
            x2={left}
            y2={top + innerH}
            stroke={COLORS.hairlineSoft}
          />
        )}

        {perDomain && (
          <line x1={0} y1={top + innerH} x2={width} y2={top + innerH} stroke={COLORS.hairline} />
        )}

        {points.map((p, index) => (
          <circle
            key={`pt-${p.schoolYear}`}
            cx={x(index)}
            cy={y(p.value)}
            r={dotRadius}
            fill={perDomain ? accentColor : COLORS.navy}
            stroke="#FFFFFF"
            strokeWidth={2}
          />
        ))}

        {perDomain &&
          points.map((p, index) => (
            <text
              key={`value-${p.schoolYear}`}
              x={x(index)}
              y={y(p.value) - valueGap}
              textAnchor="middle"
              fill={COLORS.muted}
              fontSize={fontSize}
              fontWeight={500}
            >
              {formatValue(p.value)}
            </text>
          ))}

        {Array.from({ length: slotCount }, (_, index) => {
          const isMeasured = index < points.length;
          return (
            <text
              key={`xlabel-${index}`}
              x={x(index)}
              y={height - 12}
              textAnchor="middle"
              fill={perDomain ? COLORS.muted : isMeasured ? COLORS.navy : COLORS.faint}
              fontSize={fontSize}
              fontWeight={perDomain ? 500 : isMeasured ? 700 : 600}
            >
              {formatSchoolYearShort(slotSchoolYear(points, index))}
            </text>
          );
        })}
      </svg>
    </figure>
  );
}
